import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { getClient, rpc, translateError } from "@vixio/data";
import { Button, Callout, DescriptionList, TextInput, formatDateTime } from "@vixio/ui";
import { AuthLayout } from "../auth/AuthLayout";
import { useAuth } from "../auth/AuthProvider";

interface Preview {
  organization: string;
  role: string;
  role_label: string;
  email_hint: string;
  status: "pending" | "accepted" | "revoked" | "expired";
  expires_at: string;
}

const STATUS: Record<Preview["status"], string> = {
  pending: "aguardando aceite",
  accepted: "já aceito",
  revoked: "revogado",
  expired: "vencido",
};

export function InvitePage() {
  const { token = "" } = useParams();
  const { session, needsPassword } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const preview = useQuery({
    queryKey: ["public", "invitation", token],
    queryFn: async () => (await rpc("core", "invitation_preview", { p_token: token })) as Preview,
    retry: false,
  });

  async function accept() {
    setBusy(true);
    setError(null);
    try {
      const orgId = (await rpc("core", "accept_invitation", { p_token: token })) as string;
      try {
        localStorage.setItem("vixio.org", orgId);
      } catch {
        /* ignore */
      }
      // Cliente: primeiro acesso cria a senha (se entrou só pelo link) e segue para o portal.
      const client = preview.data?.role === "client";
      window.location.assign(client ? (needsPassword ? "/primeiro-acesso" : "/portal") : "/");
    } catch (e) {
      setError(translateError(e).message);
    } finally {
      setBusy(false);
    }
  }

  if (preview.isLoading) return <AuthLayout title="Convite">{null}</AuthLayout>;
  if (preview.error || !preview.data) {
    return (
      <AuthLayout title="Convite inválido" area="portal">
        <Callout tone="crit">{preview.error ? translateError(preview.error).message : "Convite não encontrado."}</Callout>
      </AuthLayout>
    );
  }
  const p = preview.data;
  const client = p.role === "client";
  return (
    <AuthLayout
      title={client ? `Sua área do cliente na ${p.organization}` : `Convite para ${p.organization}`}
      subtitle={
        client
          ? "Você foi convidado para acompanhar seus projetos, entregas, faturas e reuniões num lugar só."
          : "Você foi convidado para participar desta organização no Vixio OS."
      }
      area={client ? "portal" : "panel"}
    >
      <div className="vx-stack">
        <DescriptionList
          items={[
            ["Organização", p.organization],
            ...(client ? [] : [["Papel", p.role_label] as [string, string]]),
            ["Enviado para", <span className="mono">{p.email_hint}</span>],
            ["Situação", STATUS[p.status]],
            ["Vale até", formatDateTime(p.expires_at)],
          ]}
        />
        {p.status !== "pending" ? (
          <Callout tone="warn">Este convite está {STATUS[p.status]}. Peça um novo a quem administra a organização.</Callout>
        ) : session ? (
          <>
            <p className="text-2">
              Conectado como <strong>{session.user.email}</strong>. O convite só vale para o e-mail que o recebeu.
            </p>
            {error ? <Callout tone="crit">{error}</Callout> : null}
            <div className="vx-row">
              <Button variant="primary" onClick={accept} loading={busy}>
                {client ? "Aceitar e entrar" : "Aceitar convite"}
              </Button>
              <Button
                onClick={async () => {
                  await getClient().auth.signOut();
                  navigate(0);
                }}
              >
                Usar outra conta
              </Button>
            </div>
          </>
        ) : sent ? (
          <Callout tone="ok" title="Confira seu e-mail">
            Enviamos um link para confirmar que é você. Abra o e-mail e siga o link: você volta para esta página já conectado
            para aceitar{client ? " e criar sua senha" : ""}.
          </Callout>
        ) : (
          <form
            className="vx-stack"
            noValidate
            onSubmit={async (e) => {
              e.preventDefault();
              setError(null);
              setBusy(true);
              try {
                const { error: err } = await getClient().auth.signInWithOtp({
                  email: email.trim(),
                  options: { shouldCreateUser: true, emailRedirectTo: window.location.href },
                });
                if (err) throw err;
                setSent(true);
              } catch (err) {
                setError(translateError(err).message);
              } finally {
                setBusy(false);
              }
            }}
          >
            <TextInput label="Seu e-mail" type="email" value={email} onChange={setEmail} autoComplete="email" hint="O mesmo que recebeu o convite." />
            {error ? <Callout tone="crit">{error}</Callout> : null}
            <Button type="submit" variant="primary" loading={busy}>
              {client ? "Confirmar meu e-mail" : "Receber link de acesso"}
            </Button>
            <p className="text-2">
              Já tem senha? <Link to={client ? "/portal/entrar" : "/entrar"} state={{ from: `/convite/${token}` }}>Entrar</Link>
            </p>
          </form>
        )}
      </div>
    </AuthLayout>
  );
}
